import React, { useEffect, useMemo, useRef } from "react";
import {
  LoaderFunctionArgs,
  redirect,
  useLoaderData,
  useSearchParams,
} from "react-router-dom";
import { useLanguage } from "@tlc/ls2pac/components/Language";
import {
  Alert,
  AlertTitle,
  Box,
  Card,
  CardContent,
  CardHeader,
  FormControl,
  Link,
  MenuItem,
  OutlinedInput,
  Pagination,
  Select,
  SelectChangeEvent,
  Skeleton,
  Toolbar,
  Typography,
} from "@mui/material";
import { LoaderData } from "../../account/MyAccount";
import { LocationConfigurationHolder } from "@tlc/ls2pac/components/LocationConfiguration";
import { WorldBookResultRecord } from "./WorldBookResultRecord";
import { useIsMobile } from "../../../../hooks/useIsMobile";
import { useWorldBookSearch } from "./WorldBookSearchProvider";
import { useNumberFormat } from "../../../../hooks/useNumberFormat";
import { SanitizedHTML } from "@tlc/ls2pac/components/SanitizedHTML";

export async function fetchWorldBookDbOptions() {
  const locationConfiguration =
    await LocationConfigurationHolder.fetchLocationConfiguration();

  const options = locationConfiguration.pacSettings.worldBookDbOptions;

  const defaultDb = options.find((option) => option.defaultDb) ?? null;

  return {
    allOptions: options,
    defaultDb,
  };
}

export async function WorldBookResultsPageLoader({
  request,
}: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const searchTerm = url.searchParams.get("term");
  const selectedDb = url.searchParams.get("db");

  const { allOptions, defaultDb } = await fetchWorldBookDbOptions();
  const isValid = allOptions.some((option) => option.value === selectedDb);

  if (selectedDb && !isValid) {
    url.searchParams.delete("db");
    return redirect(url.toString());
  }

  const activeDb = isValid ? selectedDb : defaultDb?.value;

  const payload = {
    dbOptions: {
      all: allOptions,
      default: defaultDb,
      selected: activeDb,
    },
    searchTerm,
  };

  return { payload };
}

interface WorldBookResultDisplayProps {
  record: WorldBookResultRecord;
}
export const WorldBookResultDisplay: React.FC<WorldBookResultDisplayProps> = ({
  record,
}) => {
  const { title, summary, persistentLink, category } = record;

  const titleLink = useMemo(() => {
    if (persistentLink) {
      return (
        <Link
          href={persistentLink}
          target="_blank"
          rel="noopener noreferrer"
          component="a"
          underline="hover"
        >
          <SanitizedHTML html={title} />
        </Link>
      );
    }
    return title;
  }, [persistentLink, title]);

  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        slotProps={{
          title: {
            component: "h3",
          },
        }}
        title={titleLink}
        subheader={
          <Typography component="h4" color="text.secondary">
            {category}
          </Typography>
        }
      />
      <CardContent>
        <Typography component="p">
          <SanitizedHTML html={summary} />
        </Typography>
      </CardContent>
    </Card>
  );
};

export const WorldBookResultsPage = () => {
  const {
    searchParams: { term, page },
    setPage,
    totalHits,
    results,
    loadingResults,
    pageSize,
  } = useWorldBookSearch();

  const { format } = useNumberFormat();

  const { payload } = useLoaderData() as LoaderData<
    typeof WorldBookResultsPageLoader
  >;

  const allOptions = payload.dbOptions.all;
  const defaultDb = payload.dbOptions.default;
  const selected = payload.dbOptions.selected;

  const [searchParams, setSearchParams] = useSearchParams();
  const { translate } = useLanguage();

  const isMobile = useIsMobile();

  const handleDbChange = (event: SelectChangeEvent<string>) => {
    const selectedValue = event.target.value;
    const newParams = new URLSearchParams(searchParams);

    if (selectedValue && selectedValue !== defaultDb?.value) {
      newParams.set("db", selectedValue);
    } else {
      newParams.delete("db");
    }

    newParams.set("page", "1");
    setSearchParams(newParams);
  };

  return (
    <Box>
      <Toolbar
        sx={{
          flexWrap: "wrap",
          gap: 1,
          pl: { sm: 1, xs: 1 },
          pr: { xs: 1, sm: 1 },
        }}
      >
        <Typography color="inherit" variant="h6" component="h2">
          {loadingResults ? (
            <Skeleton
              variant="text"
              width={20}
              sx={{ display: "inline-flex" }}
            />
          ) : (
            format(totalHits)
          )}{" "}
          {translate("searchResultsFor")} "{term}"
        </Typography>
      </Toolbar>
      <Toolbar disableGutters sx={{ px: 1, justifyContent: "flex-start" }}>
        <FormControl fullWidth={isMobile}>
          <Select
            size="small"
            id="database-select"
            value={selected}
            onChange={handleDbChange}
            disabled={loadingResults}
            input={<OutlinedInput notched={false} />}
          >
            {defaultDb && (
              <MenuItem value={defaultDb.value}>{defaultDb.name}</MenuItem>
            )}

            {allOptions
              .filter((option) => !option.defaultDb && option.useThis) // only display enabled databases
              .map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.name}
                </MenuItem>
              ))}
          </Select>
        </FormControl>
      </Toolbar>

      {loadingResults ? (
        <>
          {Array.from({ length: pageSize }).map((_, index) => (
            <div key={index}>
              <WorldBookResultSkeleton />
            </div>
          ))}
        </>
      ) : (
        <>
          {totalHits > 0 ? (
            results.map((result) => (
              <div key={result.docid}>
                <WorldBookResultDisplay record={result} />
              </div>
            ))
          ) : (
            <Alert severity="info">
              <AlertTitle>{translate("searchErrorMessage")}</AlertTitle>
            </Alert>
          )}
        </>
      )}

      {totalHits > pageSize && (
        <Pagination
          page={page}
          count={Math.ceil(totalHits / pageSize)}
          onChange={(_: React.ChangeEvent<unknown>, value: number) => {
            setPage(value);
          }}
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            paddingTop: "30px",
            paddingBottom: "50px",
          }}
        />
      )}
    </Box>
  );
};

export const WorldBookResultSkeleton: React.FC = () => {
  return (
    <Card sx={{ mb: 2 }}>
      <CardHeader
        title={<Skeleton animation="wave" height={14} width="25%" />}
        subheader={
          <Skeleton animation="wave" height={10} width="10%" sx={{ pt: 1 }} />
        }
      />
      <CardContent>
        <Skeleton animation="wave" height={10} width="65%" />
      </CardContent>
    </Card>
  );
};
